import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  DevicesCategory,
  IndexedParticipation,
  ParticipantFieldType,
  StudyType,
} from '@app/api';
import { IconChip } from '@app/core';
import { useParticipationTableContext } from './ParticipationTableContext';
import { TableColumnHeader } from './TableColumnHeader';
import { TableParticipationHeader } from '../TableParticipationHeader';
import { Filter } from '../Filter';
import { DEVICE_TYPE_ICONS } from '../../../../EditSession/components/SessionTechnicalNeeds';

const useStyles = createUseStyles((theme: Theme) => ({
  cellContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    maxWidth: theme.marginBase * 35,
  },
}));

export const DeviceHeader = () => {
  const { t } = useTranslation();
  const { session, sessionHaveDeviceQuota, filtering } =
    useParticipationTableContext();

  if (session.studyType !== StudyType.UNMODERATED && !sessionHaveDeviceQuota) {
    return null;
  }

  return (
    <th key="usedDevice">
      <TableColumnHeader>
        <TableParticipationHeader
          fieldKey="usedDevice"
          fieldType={ParticipantFieldType.SHORT_TEXT}
        >
          <span>
            {t('sessions.details.participantsDetails.columns.device')}
          </span>
        </TableParticipationHeader>

        <Filter
          fieldRef="selectedDevice"
          fieldLabel={t('sessions.details.participantsDetails.columns.device')}
          fieldType={ParticipantFieldType.SINGLE_CHOICE}
          fieldValues={Object.values(DevicesCategory).map((d) => ({
            label: t(`sessions.display.allowedDevicesCategoriesChoices.${d}`),
            color: 'blue',
            value: d,
          }))}
          sessionId={session.id}
          currentFilterData={filtering.getFieldCurrentFilter('selectedDevice')}
          onFilterDataChange={filtering.handleFilterChange}
        />
      </TableColumnHeader>
    </th>
  );
};

export const DeviceCell = ({
  participation,
}: {
  participation: IndexedParticipation;
}) => {
  const { t } = useTranslation();
  const { session, sessionHaveDeviceQuota } = useParticipationTableContext();
  const classes = useStyles();

  if (session.studyType !== StudyType.UNMODERATED && !sessionHaveDeviceQuota) {
    return null;
  }

  return (
    <td key="usedDevice" className={classes.cellContainer}>
      {participation.selectedDevice ? (
        <IconChip
          icon={DEVICE_TYPE_ICONS[participation.selectedDevice]}
          alt={t(
            `sessions.display.allowedDevicesCategoriesChoices.${participation.selectedDevice}`,
          )}
        />
      ) : (
        participation.formResponses
          ?.find((fr) => fr.fieldRef === 'ALLOWED_DEVICES_CATEGORY')
          ?.values.map((deviceRef) => (
            <IconChip icon={DEVICE_TYPE_ICONS[deviceRef as DevicesCategory]} />
          ))
      )}
    </td>
  );
};
